import { selectAPI } from '@api/API.sagas';
import {
    fetchCreatePaymentIntent,
    fetchCreateTruelayerPayment
} from '@api/lendingAPI/LendingAPI';
import {
    API_GET_EARLY_REPAYMENT_SIMULATION,
    API_GET_EARLY_REPAYMENT_SIMULATION_SUCCESS,
    API_GET_PAYMENT_METHOD,
    API_GET_PAYMENT_METHOD_SUCCESS
} from '@api/lendingAPI/LendingAPI.actions';
import { CLOSE_MODAL } from '@iwModals/Modals.actions';
import { GoogleTagManager } from '@iwoca/frontend-tracking-library';
import { getCurrentLocation } from '@iwRouter/HistoryProvider';
import { REQUEST_CHANGE_ROUTER, UPDATE_URL } from '@iwRouter/IwRouter.actions';
import _ from 'lodash';
import {
    all,
    call,
    fork,
    put,
    select,
    take,
    takeLatest
} from 'redux-saga/effects';

import { FLEXI_12_URL } from '../../dashboard/Dashboard.sagas';
import { MANUAL_PAYMENT } from '../../modals/manualPaymentModal/ManualPaymentModal.actions';
import {
    PROCESSING_PAYMENT_MODAL_RETURN_TO_DASHBOARD,
    PROCESSING_PAYMENT_MODAL_TRY_AGAIN,
    RESET_PROCESSING_PAYMENT_MODAL,
    SHOW_PROCESING_PAYMENT_MODAL,
    SHOW_PROCESING_PAYMENT_MODAL_ERROR
} from '../../modals/processingPaymentModal/ProcessingPaymentModal.actions';
import {
    getAccountByAccountId,
    isPrimaryAccountCBILS
} from '../../multiLoanDashboard/accountsSelector';
import {
    CONFIRM_EARLY_REPAYMENT,
    CONFIRM_EARLY_REPAYMENT_LOADING,
    DEBIT_CARD_REPAYMENT,
    EARLY_REPAYMENT_CONFIRM_REPAYMENT_DATA,
    SHOW_CHOOSE_REPAYMENT_AMOUNT_STEP,
    SHOW_CONFIRM_REPAYMENT_STEP,
    TRUELAYER_REPAYMENT,
    UPDATE_CONFIRM_REPAYMENT_SCHEDULE_EARLY_REPAYMENT,
    UPDATE_SELECTED_REPAYMENT_AMOUNT
} from '../EarlyRepayment.actions';
import {
    DEBIT_CARD_TYPE,
    getEarlyRepaymentURLData,
    MANUAL_BANK_TRANSFER_TYPE,
    serialiseInputAmount,
    TRUELAYER_REPAYMENT_TYPE
} from '../EarlyRepaymentHelper';
import { confirmCheckoutPaymentIntent } from './ConfirmRepayment.sagas.checkout';
import { confirmStripePaymentIntent } from './ConfirmRepayment.sagas.stripe';

export const confirmRepaymentSagaWorkers = [
    fork(earlyRepaymentShowConfirmStepWorker),
    fork(confirmEarlyRepaymentStepWorker),
    fork(debitCardPayment),
    fork(truelayerRepayment)
];

function* earlyRepaymentShowConfirmStepWorker() {
    yield takeLatest(SHOW_CONFIRM_REPAYMENT_STEP, function* () {
        const currentLocation = getCurrentLocation();
        const urlData = getEarlyRepaymentURLData(currentLocation);
        const { accountId } = urlData;

        let amount;
        if (isNaN(urlData.amount)) {
            amount = yield select(
                ({ earlyRepayment }) => earlyRepayment.selectedInputAmount
            );

            if (!_.isNumber(amount)) {
                yield put({ type: SHOW_CHOOSE_REPAYMENT_AMOUNT_STEP });
                //TODO: this should cancel this forked worker
                return;
            }
        } else {
            amount = urlData.amount;
        }

        // If no account ID in URL, redirect customer
        if (!accountId) {
            return yield put({
                type: REQUEST_CHANGE_ROUTER,
                location: { pathname: FLEXI_12_URL }
            });
        }

        const account = yield select((state) =>
            getAccountByAccountId(state, accountId)
        );

        const outstandingBalance = account.balance.total;

        const serialisedInputAmount = serialiseInputAmount(
            amount,
            outstandingBalance
        );

        yield put({
            type: UPDATE_SELECTED_REPAYMENT_AMOUNT,
            inputAmount: serialisedInputAmount,
            outstandingBalance
        });

        yield all([
            put({
                type: UPDATE_URL,
                pathname: '/repayment/confirm/',
                search: `?amount=${serialisedInputAmount}&accountId=${urlData.accountId}`
            }),
            put({ type: API_GET_PAYMENT_METHOD })
        ]);
        yield take(API_GET_PAYMENT_METHOD_SUCCESS);
        yield put({
            type: API_GET_EARLY_REPAYMENT_SIMULATION,
            accountId,
            amount: 0
        });
        const initial = yield take(API_GET_EARLY_REPAYMENT_SIMULATION_SUCCESS);

        yield put({
            type: API_GET_EARLY_REPAYMENT_SIMULATION,
            accountId,
            amount: serialisedInputAmount
        });
        const current = yield take(API_GET_EARLY_REPAYMENT_SIMULATION_SUCCESS);

        const { payment_method } = yield selectAPI();

        yield put({
            type: EARLY_REPAYMENT_CONFIRM_REPAYMENT_DATA
        });

        yield put({
            type: UPDATE_CONFIRM_REPAYMENT_SCHEDULE_EARLY_REPAYMENT,
            payment_method,
            initial,
            current
        });
    });
}

function* confirmEarlyRepaymentStepWorker() {
    yield takeLatest(CONFIRM_EARLY_REPAYMENT, function* (action) {
        yield put({ type: CONFIRM_EARLY_REPAYMENT_LOADING });

        const amount = yield select(
            ({ earlyRepayment }) => earlyRepayment.selectedInputAmount
        );

        const selectedPayment = action.paymentType
            ? action.paymentType
            : yield select(
                  ({ earlyRepayment }) => earlyRepayment.selectedPayment
              );

        const idempotencyKey = yield select(
            ({ earlyRepayment }) => earlyRepayment.earlyRepaymentIdempotencyKey
        );

        const urlData = getEarlyRepaymentURLData(getCurrentLocation());
        switch (selectedPayment.type) {
            case DEBIT_CARD_TYPE:
                yield put({
                    type: DEBIT_CARD_REPAYMENT,
                    selectedPayment,
                    amount,
                    accountId: urlData.accountId,
                    stripe: action.stripe
                });
                break;
            case MANUAL_BANK_TRANSFER_TYPE:
                const account = yield select((state) =>
                    getAccountByAccountId(state, urlData.accountId)
                );

                const bankDetails =
                    account.repayment_details.bank_transfer_details;

                yield put({
                    type: MANUAL_PAYMENT,
                    amount,
                    accountNumber: bankDetails.account_number,
                    sortCode: bankDetails.sort_code,
                    iban: bankDetails.iban,
                    name: bankDetails.owner_name,
                    reference: bankDetails.reference
                });
                break;
            case TRUELAYER_REPAYMENT_TYPE:
                yield put({
                    type: TRUELAYER_REPAYMENT,
                    amount: amount,
                    accountId: urlData.accountId,
                    idempotencyKey: idempotencyKey
                });
                break;
        }

        GoogleTagManager.trackEvent({
            event: 'Confirm early repayment',
            value: amount
        });
    });
}

function* debitCardPayment() {
    yield takeLatest(DEBIT_CARD_REPAYMENT, function* (action) {
        yield put({
            type: SHOW_PROCESING_PAYMENT_MODAL,
            amount: action.amount,
            paymentMethod: action.selectedPayment.cardLastDigits,
            mode: 'early_repayment'
        });

        yield paymentWithSCA(action);

        const nextAction = yield take([
            PROCESSING_PAYMENT_MODAL_TRY_AGAIN,
            PROCESSING_PAYMENT_MODAL_RETURN_TO_DASHBOARD
        ]);

        switch (nextAction.type) {
            case PROCESSING_PAYMENT_MODAL_TRY_AGAIN:
                yield put({
                    type: REQUEST_CHANGE_ROUTER,
                    location: {
                        pathname: `/repayment/confirm/?amount=${action.amount}&accountId=${action.accountId}`
                    }
                });
                yield resetProcessingPaymentAndClose();
                break;
            case PROCESSING_PAYMENT_MODAL_RETURN_TO_DASHBOARD:
                const primaryAccountCBILS = yield select(isPrimaryAccountCBILS);
                yield resetProcessingPaymentAndClose();

                if (primaryAccountCBILS) {
                    return yield put({
                        type: REQUEST_CHANGE_ROUTER,
                        location: { pathname: '/CBILS/' }
                    });
                }

                yield put({
                    type: REQUEST_CHANGE_ROUTER,
                    location: { pathname: FLEXI_12_URL }
                });
                break;
        }
    });
}

function* resetProcessingPaymentAndClose() {
    yield put({ type: RESET_PROCESSING_PAYMENT_MODAL });
    yield put({ type: CLOSE_MODAL });
}

function* paymentWithSCA({ stripe, amount, accountId, selectedPayment }) {
    const body = JSON.stringify({
        data: {
            amount: amount,
            payment_method_id: selectedPayment.value,
            account_id: accountId
        }
    });

    try {
        const [response] = yield call(fetchCreatePaymentIntent, { body });

        switch (response.data.payment_processor.toLowerCase()) {
            case 'stripe':
                yield confirmStripePaymentIntent(stripe, response);
                break;
            case 'checkout':
                yield confirmCheckoutPaymentIntent(response);
                break;
            default:
                return yield put({
                    type: SHOW_PROCESING_PAYMENT_MODAL_ERROR,
                    extra: `Invalid payment processor: ${response.data.payment_processor}!`
                });
        }
    } catch (errorResponse) {
        return yield put({
            type: SHOW_PROCESING_PAYMENT_MODAL_ERROR,
            extra: errorResponse
        });
    }
}

function* truelayerRepayment() {
    yield takeLatest(TRUELAYER_REPAYMENT, function* (action) {
        yield put({
            type: SHOW_PROCESING_PAYMENT_MODAL,
            amount: action.amount,
            paymentMethod: TRUELAYER_REPAYMENT_TYPE,
            mode: 'early_repayment'
        });

        const body = JSON.stringify({
            data: {
                idempotency_key: action.idempotencyKey,
                account_id: action.accountId,
                amount: action.amount
            }
        });

        try {
            const [response] = yield call(fetchCreateTruelayerPayment, {
                body
            });

            const redirectUrl = response.data.redirect_url;

            window.location.href = redirectUrl;
        } catch (errorResponse) {
            return yield put({
                type: SHOW_PROCESING_PAYMENT_MODAL_ERROR,
                extra: errorResponse
            });
        }
    });
}
